import { type Security, makeTypeGuardByPropertyValue } from '@ipfabric/utils';
import type { BoxTreeNodeComponentProps } from 'components/BoxTree/BoxTreeNodeComponentProps';
import type { SecuritySchemaTreeContext } from 'components/SecuritySchema/SecuritySchemaTreeContext';
import { array } from 'fp-ts';

export type TestMetaInfo = {
  name?: string;
  category?: string;
  description?: string;
};

export type ReferenceMetaInfo = TestMetaInfo & {
  reference: Security.TestReference;
};

type SecuritySchemaNodeBase = {
  children: SecuritySchemaNode[];
};

type TestNodeBase = SecuritySchemaNodeBase & {
  info: TestMetaInfo;
  referenceInfo?: ReferenceMetaInfo;
};

type GroupTestType = 'and' | 'not' | 'or';

export type ErrorType =
  // test refers (transitively) to itself, so there is no actual test to resolve the reference to
  | 'circularReference'

  // this reference has already occurred on ancestor path in the tree, so rendering
  // would result in infinitely deep tree
  | 'circularRecursionReference'
  | 'notFoundReference'

  // unsupported data returned from API or resulted from app errors
  | 'unknownNode'
  | 'unresolvedReference';

export type ErrorNode = SecuritySchemaNodeBase & {
  errorType: ErrorType;
  referenceInfo?: ReferenceMetaInfo;
  type: 'error';
};

export const isErrorNode = makeTypeGuardByPropertyValue<
  SecuritySchemaNode,
  ErrorNode
>()('type', 'error');

export const unknownNodeError: ErrorNode = {
  children: array.empty,
  errorType: 'unknownNode',
  type: 'error',
};

export type GroupTestNode = TestNodeBase & {
  group: GroupTestType;
  type: 'group';
};

export const isGroupTestNode = makeTypeGuardByPropertyValue<
  SecuritySchemaNode,
  GroupTestNode
>()('type', 'group');

/**
 * Position among the chain items on the current page. Highlighting and
 * scroll-to-rule key off this rather than the tree path, which shifts when
 * rules get wrapped in a {@link PolicyNode}.
 */
type PagedChainItem = { pageIndex?: number };

export type RuleNode = SecuritySchemaNodeBase &
  PagedChainItem & {
    type: 'rule';
    action: Security.Action;
    active: boolean;
    cloudPolicy?: Security.CloudPolicyRef;
    description?: string;
    name?: string;
    /** Cloud rules only: the vendor's evaluation order (NSG / NACL rule number). */
    priority?: number;
    /**
     * Mark a rule that represents default action
     *
     * Default action is not a rule in data, but we display it that way as it is logically
     * "the last rule in the chain that always matches if no previous rule matched".
     */
    defaultAction?: boolean;
  };

export const isRuleNode = makeTypeGuardByPropertyValue<
  SecuritySchemaNode,
  RuleNode
>()('type', 'rule');

/**
 * A cloud policy (security group / NACL / NSG / firewall policy) wrapping the
 * chain's rules authored in it. Enforcement points merge several policies into
 * one chain, so without this a rule gives no clue where it came from.
 */
export type PolicyNode = SecuritySchemaNodeBase &
  PagedChainItem & {
    type: 'policy';
    policy: Security.CloudPolicyRef;
    /**
     * Set when the policy's rules are a chain of their own rather than children
     * here (an enforcement point evaluated in stages). The box links into that
     * chain and stands in for a chain item, so it takes a `pageIndex` and can be
     * highlighted like a rule.
     */
    chainName?: string;
  };

export const isPolicyNode = makeTypeGuardByPropertyValue<
  SecuritySchemaNode,
  PolicyNode
>()('type', 'policy');

/**
 * A node standing for one item of the chain being displayed, so it can be
 * numbered ({@link PagedChainItem}) and highlighted. A policy box qualifies
 * only when it replaces a chain rule; one created by grouping rules does not,
 * which is why the number is assigned before grouping.
 */
export const isChainItemNode = (
  node: SecuritySchemaNode,
): node is RuleNode | PolicyNode => isRuleNode(node) || isPolicyNode(node);

export type ComparisonTestNode = TestNodeBase & {
  left: Security.PacketSelector;
  operator: Security.ComparisonTest['operator'];

  // omitted = reference resolution failure
  right?: Security.LiteralValue;
  rightValueReference?: {
    dictionary: 'local' | 'objectGroup';
    key: string;
  };
  type: 'comparison';
};

export const isComparisonTestNode = makeTypeGuardByPropertyValue<
  SecuritySchemaNode,
  ComparisonTestNode
>()('type', 'comparison');

export type TrivialTestNode = TestNodeBase & {
  operator: Security.TrivialTest['operator'];
  original?: string;
  type: 'trivial';
};

export const isTrivialTestNode = makeTypeGuardByPropertyValue<
  SecuritySchemaNode,
  TrivialTestNode
>()('type', 'trivial');

export type EvalTestNode = TestNodeBase & {
  chainName: string;
  chainType: Security.SecurityType;
  checkType: 'passing' | 'failing';
  type: 'eval';
};

export const isEvalTestNode = makeTypeGuardByPropertyValue<
  SecuritySchemaNode,
  EvalTestNode
>()('type', 'eval');

export type TestNode =
  | ComparisonTestNode
  | GroupTestNode
  | TrivialTestNode
  | EvalTestNode;

export type PathLookupOptionsNode = SecuritySchemaNodeBase & {
  type: 'pathLookupOptions';
  pathLookupOptions: Security.PathLookupOption[];
};

export const isPathLookupOptionsNode = makeTypeGuardByPropertyValue<
  SecuritySchemaNode,
  PathLookupOptionsNode
>()('type', 'pathLookupOptions');

export type SecuritySchemaNode =
  | ErrorNode
  | PolicyNode
  | RuleNode
  | TestNode
  | PathLookupOptionsNode;

export type SecuritySchemaNodeComponentProps<
  TSchemaNode extends SecuritySchemaNode = SecuritySchemaNode,
> = BoxTreeNodeComponentProps<TSchemaNode, SecuritySchemaTreeContext>;
